/**
 * Pantone simulation library for the variants swatch + picker.
 *
 * A small, opinionated set of the most frequently quoted apparel codes
 * (Pantone Solid Coated basics + popular TCX/TPG fashion codes), each with a
 * display name so the picker can search by colour name as well as code. Hex
 * values are approximations on a sRGB monitor; Pantone is a licensed
 * reference system and exact matching is not the goal here — unknown codes
 * render as a neutral chip with no swatch and operators see the code text
 * either way.
 *
 * Add a code: append an entry below, code normalised to UPPERCASE with single
 * spaces. Lookups are case-insensitive, tolerant of extra whitespace, of a
 * missing "PMS" prefix, and of TPG/TCX/TPX suffix drift (a "19-0915 TPG"
 * quote resolves to the 19-0915 TCX entry — the two systems' colours are
 * near-identical on screen).
 */

export type PantoneEntry = {
  /** Canonical code as stored, e.g. "PMS 19-0915 TCX". */
  code: string;
  /** Pantone's colour name, e.g. "Coffee Bean" — searchable in the picker. */
  name: string;
  /** sRGB approximation for on-screen simulation. */
  hex: string;
};

export const PANTONE_LIBRARY: readonly PantoneEntry[] = [
  // Solid Coated — primaries
  { code: 'PMS BLACK 6 C', name: 'Black 6', hex: '#101820' },
  { code: 'PMS BLACK C', name: 'Black', hex: '#2D2926' },
  { code: 'PMS WHITE', name: 'White', hex: '#FFFFFF' },
  { code: 'PMS COOL GRAY 1 C', name: 'Cool Gray 1', hex: '#D9D9D6' },
  { code: 'PMS COOL GRAY 4 C', name: 'Cool Gray 4', hex: '#BBBCBC' },
  { code: 'PMS COOL GRAY 7 C', name: 'Cool Gray 7', hex: '#97999B' },
  { code: 'PMS COOL GRAY 11 C', name: 'Cool Gray 11', hex: '#53565A' },
  { code: 'PMS WARM GRAY 6 C', name: 'Warm Gray 6', hex: '#A39E93' },
  // Reds / Oranges
  { code: 'PMS 18-1664 TPG', name: 'True Red', hex: '#C8102E' },
  { code: 'PMS 19-1664 TCX', name: 'Chinese Red', hex: '#BB133E' },
  { code: 'PMS 18-1438 TCX', name: 'Marsala', hex: '#A85751' },
  { code: 'PMS 16-1546 TCX', name: 'Cherry Tomato', hex: '#E25C46' },
  { code: 'PMS 17-1463 TCX', name: 'Coral', hex: '#FF6F61' },
  { code: 'PMS 18-1248 TCX', name: 'Rust', hex: '#9C4A1A' },
  { code: 'PMS 17-1342 TCX', name: 'Sandstone', hex: '#B66A50' },
  { code: 'PMS 18-1142 TPG', name: 'Cognac', hex: '#80502D' },
  { code: 'PMS 17-1456 TCX', name: 'Burnt Orange', hex: '#E36F47' },
  // Yellows / Olives / Browns
  { code: 'PMS 13-0859 TCX', name: 'Yellow Cream', hex: '#F0CC4A' },
  { code: 'PMS 14-0848 TCX', name: 'Mineral Yellow', hex: '#F1B82D' },
  { code: 'PMS 16-0945 TCX', name: 'Bone Brown', hex: '#B5915D' },
  { code: 'PMS 18-0625 TCX', name: 'Capulet Olive', hex: '#776F4E' },
  { code: 'PMS 19-0815 TCX', name: 'Carafe', hex: '#544741' },
  { code: 'PMS 18-0312 TCX', name: 'Deep Lichen Green', hex: '#5D5D45' },
  { code: 'PMS 19-0915 TCX', name: 'Coffee Bean', hex: '#392F2A' },
  // Greens
  { code: 'PMS 18-5845 TCX', name: 'Jolly Green', hex: '#00854A' },
  { code: 'PMS 19-5413 TCX', name: 'Forest Biome', hex: '#1B4332' },
  { code: 'PMS 17-6333 TCX', name: 'Greenery', hex: '#5A8F4C' },
  { code: 'PMS 16-0228 TCX', name: 'Foliage Green', hex: '#9BB07C' },
  { code: 'PMS 14-0223 TCX', name: 'Pale Green', hex: '#C2D29F' },
  { code: 'PMS 18-5424 TCX', name: 'Eden', hex: '#1F4738' },
  { code: 'PMS 16-5917 TCX', name: 'Malachite Green', hex: '#489A80' },
  // Blues
  { code: 'PMS 19-4052 TCX', name: 'Classic Blue', hex: '#0F4C81' },
  { code: 'PMS 19-3933 TCX', name: 'Estate Blue', hex: '#1E2A52' },
  { code: 'PMS 18-4045 TCX', name: 'Brilliant Blue', hex: '#1B72B6' },
  { code: 'PMS 16-4132 TCX', name: 'Airy Blue', hex: '#7FB3D5' },
  { code: 'PMS 14-4318 TCX', name: 'Crystal Blue', hex: '#9BC4D7' },
  { code: 'PMS 19-4005 TCX', name: 'Caviar', hex: '#2C2E32' },
  // Indigos / Purples
  { code: 'PMS 19-3815 TCX', name: 'Astral Aura', hex: '#3F3B5C' },
  { code: 'PMS 18-3838 TCX', name: 'Ultra Violet', hex: '#5A4E8A' },
  { code: 'PMS 17-3617 TCX', name: 'Argyle Purple', hex: '#7E5F8A' },
  // Pinks
  { code: 'PMS 17-1356 TCX', name: 'Tea Rose', hex: '#E27688' },
  { code: 'PMS 13-2807 TCX', name: 'Cherry Blossom', hex: '#F2C2C8' },
  { code: 'PMS 14-1714 TCX', name: 'Quartz Pink', hex: '#F4ABB5' },
  // Neutrals / Beiges
  { code: 'PMS 11-0103 TCX', name: 'Egret', hex: '#F0EBE0' },
  { code: 'PMS 14-1116 TCX', name: 'Pale Khaki', hex: '#D9C2A1' },
  { code: 'PMS 16-1334 TCX', name: 'Tan', hex: '#A37C4F' },
  { code: 'PMS 18-1018 TCX', name: 'Shitake', hex: '#665546' },
  // Off-blacks
  { code: 'PMS 19-3911 TCX', name: 'Phantom', hex: '#1B1A1B' },
  { code: 'PMS 19-4007 TCX', name: 'Anthracite', hex: '#15202B' },
];

const normalisePantone = (code: string): string =>
  code.trim().toUpperCase().replace(/\s+/g, ' ');

const byExactCode = new Map<string, PantoneEntry>(
  PANTONE_LIBRARY.map((e) => [normalisePantone(e.code), e]),
);

/** "19-0915" → entries (suffix-agnostic fallback for TPG/TCX/TPX drift). */
const byNumber = new Map<string, PantoneEntry[]>();
for (const entry of PANTONE_LIBRARY) {
  const m = entry.code.match(/(\d{2}-\d{4})/);
  if (!m) continue;
  const list = byNumber.get(m[1]!) ?? [];
  list.push(entry);
  byNumber.set(m[1]!, list);
}

/**
 * Resolves free text to a library entry. Tolerates a missing "PMS" prefix,
 * surrounding words ("Coffee Bean 19-0915 TPG Swatch B"), and TPG/TCX/TPX
 * suffix drift. Returns null when nothing matches.
 */
export function findPantone(raw: string | null | undefined): PantoneEntry | null {
  if (!raw) return null;
  const key = normalisePantone(raw);
  const exact = byExactCode.get(key) ?? byExactCode.get(`PMS ${key}`);
  if (exact) return exact;
  const m = key.match(/(\d{2}-\d{4})(?:\s*(TPG|TCX|TPX))?/);
  if (!m) return null;
  const candidates = byNumber.get(m[1]!);
  if (!candidates || candidates.length === 0) return null;
  const suffix = m[2];
  const suffixMatch = suffix
    ? candidates.find((c) => c.code.endsWith(` ${suffix}`))
    : undefined;
  return suffixMatch ?? candidates[0]!;
}

/** Returns a hex string for a known Pantone code, or null when unmapped. */
export function pantoneToHex(code: string | null | undefined): string | null {
  return findPantone(code)?.hex ?? null;
}

/**
 * Picker search: substring match on code or colour name. An empty term
 * returns the head of the library so the dropdown is never a dead end.
 */
export function searchPantone(term: string, limit = 8): PantoneEntry[] {
  const t = normalisePantone(term);
  if (!t) return PANTONE_LIBRARY.slice(0, limit);
  return PANTONE_LIBRARY.filter(
    (e) =>
      normalisePantone(e.code).includes(t) || e.name.toUpperCase().includes(t),
  ).slice(0, limit);
}
